import { StyledDayPicker } from './StyledDayPicker'
import Separator from 'components/core/Separator'
import styled from '@emotion/styled'
import { IconButton } from 'components/core/IconButton'
import { SPACING } from 'constants/spacing.constants'
import {
    DatePickerValue,
    DefaultDatePickerValue,
} from '../types/datePicker.types'
import {
    changeDatePreservingTime,
    convertDateToStringNoTz,
} from '../utils/datePicker.utils'
import Row from 'components/core/Row'
import { Trash2 } from 'lucide-react'
import { TimePicker } from 'components/composite/TimePicker'
import { Chevron } from './Chevron'
import Button from 'components/core/Button'

const StyledIconButton = styled(IconButton)`
    padding-left: ${SPACING.p2};
    padding-right: ${SPACING.p2};
`

const StyledActionRow = styled(Row)`
    padding-top: ${SPACING.sm};
    padding-bottom: ${SPACING.sm};
    justify-content: flex-end;
`

type CalendarWithTimeProps = {
    value?: DatePickerValue
    hasConfirmButton?: boolean
    hasClearButton?: boolean
    hasTime?: boolean
    onChange: (date: DatePickerValue) => void
    onConfirm?: () => void
}

const emptyFunction = () => undefined

export const CalendarWithTime = ({
    value,
    hasConfirmButton = true,
    hasClearButton = true,
    hasTime,
    onChange,
    onConfirm = emptyFunction,
}: CalendarWithTimeProps) => {
    let valueDate: Date | undefined
    if (typeof value === 'string' && !isNaN(Date.parse(value))) {
        valueDate = new Date(value)
    }

    /**
     * carry over the current time when a user clicks a new day
     * instead of resetting to 00:00
     */
    const _onChangeDate = (newDate: DefaultDatePickerValue) => {
        if (!newDate) return
        if (!valueDate) {
            onChange(convertDateToStringNoTz(newDate))
            return
        }

        const newDateFull = changeDatePreservingTime(valueDate, newDate)

        onChange(convertDateToStringNoTz(newDateFull))
    }

    const _onChangeTime = (date: DefaultDatePickerValue) => {
        if (!date) return

        onChange(convertDateToStringNoTz(date))
    }

    const _onClearDate = () => {
        onChange(null)
    }

    return (
        <>
            <StyledDayPicker
                mode="single"
                selected={valueDate ?? undefined}
                components={{ Chevron }}
                onDayClick={_onChangeDate}
            />
            {(hasTime || !!valueDate) && (
                <>
                    <Separator />
                    <Row
                        p="p1"
                        fullWidth
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        {hasTime && (
                            <TimePicker
                                date={valueDate || undefined}
                                onChange={_onChangeTime}
                            />
                        )}
                        <StyledActionRow gap="sm">
                            {hasClearButton && valueDate && (
                                <StyledIconButton onClick={_onClearDate}>
                                    <Trash2 size={SPACING.p2} />
                                </StyledIconButton>
                            )}
                            {hasConfirmButton && (
                                <Button variant="secondary" onClick={onConfirm}>
                                    Confirm
                                </Button>
                            )}
                        </StyledActionRow>
                    </Row>
                </>
            )}
        </>
    )
}
